import { loadPdfLib } from "./pdf_libs"

/**
 * NativeFormSubmit LiveView hook
 *
 * Handles form submission for native AcroForm fields combined with
 * custom-placed fields (signatures). Uses pdf-lib client-side to fill
 * all fields + embed signature images, then uploads via custom_submit.
 *
 * Data attributes:
 *   data-pdf-url         - URL of the source PDF
 *   data-has-signatures  - "true" if the form contains signature fields
 *   data-custom-fields   - JSON of custom field definitions (with x/y/w/h positions)
 */

// Signature inputs for custom-placed fields are named "custom_sig_<id>";
// native AcroForm signature fields keep their own names and have no id.
const customFieldIdOf = name =>
  name.startsWith("custom_sig_") ? parseInt(name.replace("custom_sig_", ""), 10) : null

const NativeFormSubmit = {
  mounted() {
    this.pdfUrl = this.el.dataset.pdfUrl
    this.hasSignatures = this.el.dataset.hasSignatures === "true"
    this.multiSigner = this.el.dataset.multiSigner === "true"

    try {
      this.customFields = JSON.parse(this.el.dataset.customFields || "[]")
    } catch (_) {
      this.customFields = []
    }

    if (this.hasSignatures) {
      this._fetchPdf()

      const form = this.el.querySelector("form")
      if (form) {
        this._submitHandler = (e) => {
          e.preventDefault()
          e.stopPropagation()
          this._handleHybridSubmit(form)
        }
        form.addEventListener("submit", this._submitHandler)
      }
    }
  },

  destroyed() {
    this._pdfBytes = null
    const form = this.el.querySelector("form")
    if (form && this._submitHandler) {
      form.removeEventListener("submit", this._submitHandler)
    }
  },

  async _fetchPdf() {
    try {
      const response = await fetch(this.pdfUrl)
      this._pdfBytes = await response.arrayBuffer()
    } catch (err) {
      console.error("Failed to pre-fetch PDF:", err)
    }
  },

  async _handleHybridSubmit(form) {
    if (!this._pdfBytes) {
      console.error("PDF not loaded yet")
      return
    }

    // Check required signature fields
    const missingFields = []
    const requiredSigs = this.customFields.filter(f =>
      f.required && (f.type === "signature" || f.type === "initials")
    )
    for (const field of requiredSigs) {
      const input = form.querySelector(`input[name="custom_sig_${field.id}"]`)
      if (!input || !input.value || !input.value.startsWith("data:")) {
        missingFields.push(field.label || field.type)
      }
    }
    if (missingFields.length > 0) {
      this.pushEvent("validation_error", { message: `Please fill in required fields: ${missingFields.join(", ")}` })
      return
    }

    const submitBtn = form.querySelector("button[type=submit]")
    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.innerHTML = `<span class="loading loading-spinner loading-sm"></span> Processing...`
    }

    // Multi-signer: send this signer's values + signature images (as data
    // URLs) and let the server compose the final PDF once everyone signed.
    if (this.multiSigner) {
      const formData = new FormData(form)
      const fieldValues = {}
      for (const [name, value] of formData.entries()) {
        if (name.startsWith("_")) continue
        fieldValues[name] = value
      }
      form.querySelectorAll("input[type=checkbox]").forEach(cb => {
        if (!cb.checked) fieldValues[cb.name] = "Off"
      })

      const signatures = []
      form.querySelectorAll("input[data-signature-field]").forEach(input => {
        if (input.value && input.value.startsWith("data:image/png")) {
          signatures.push({
            field_id: customFieldIdOf(input.name),
            field_name: input.name,
            data_url: input.value,
          })
        }
      })

      this.pushEvent("custom_submit", {
        field_values: fieldValues,
        signatures,
        // A signature only exists once Accept was clicked under the consent line.
        esign_consent: signatures.length > 0,
      })
      return
    }

    try {
      const { PDFDocument, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup } = await loadPdfLib()
      const pdfDoc = await PDFDocument.load(this._pdfBytes.slice(0))
      const pages = pdfDoc.getPages()
      const pdfForm = pdfDoc.getForm()

      // Collect form values from inputs
      const formData = new FormData(form)
      const fieldValues = {}
      for (const [name, value] of formData.entries()) {
        if (name.startsWith("_")) continue
        fieldValues[name] = value
      }

      // Collect unchecked checkboxes
      form.querySelectorAll("input[type=checkbox]").forEach(cb => {
        if (!cb.checked) {
          fieldValues[cb.name] = "Off"
        }
      })

      // Fill native AcroForm fields using pdf-lib's form API
      try {
        const fields = pdfForm.getFields()
        for (const field of fields) {
          const name = field.getName()
          const value = fieldValues[name]
          if (value === undefined) continue

          try {
            if (field instanceof PDFTextField) {
              field.setText(value)
            } else if (field instanceof PDFCheckBox) {
              if (value === "Yes") field.check()
              else field.uncheck()
            } else if (field instanceof PDFDropdown) {
              if (value) field.select(value)
            } else if (field instanceof PDFRadioGroup) {
              if (value) field.select(value)
            }
          } catch (_) {}
        }
      } catch (_) {}

      // Collect all signature data and embed images
      const signatureInputs = form.querySelectorAll("input[data-signature-field]")
      const signatures = []
      const allFields = pdfForm.getFields()

      for (const input of signatureInputs) {
        const dataUrl = input.value
        if (!dataUrl || !dataUrl.startsWith("data:image/png")) continue

        const fieldName = input.name
        const customFieldId = customFieldIdOf(fieldName)
        signatures.push({ field_id: customFieldId, field_name: fieldName, data_url: dataUrl })

        const base64 = dataUrl.split(",")[1]
        const imgBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
        const pngImage = await pdfDoc.embedPng(imgBytes)

        // Check if this is a custom-placed field (has x/y/w/h coordinates)
        const customField = customFieldId
          ? this.customFields.find(f => f.id === customFieldId)
          : null

        if (customField) {
          const pageIndex = (customField.page || 1) - 1
          if (pageIndex < pages.length) {
            const page = pages[pageIndex]
            const pageHeight = page.getHeight()
            const pdfY = pageHeight - customField.y - customField.h
            this._drawScaledImage(page, pngImage, {
              x: customField.x, y: pdfY, w: customField.w, h: customField.h,
            })
          }
        } else {
          // Try to find native AcroForm signature field widget position
          try {
            const sigField = allFields.find(f => f.getName() === fieldName)
            if (sigField) {
              const widgets = sigField.acroField.getWidgets()
              if (widgets.length > 0) {
                const widget = widgets[0]
                const rect = widget.getRectangle()
                const pageRef = widget.P()
                let pageIndex = 0
                if (pageRef) {
                  pageIndex = pages.findIndex(p => p.ref === pageRef)
                  if (pageIndex < 0) pageIndex = 0
                }
                this._drawScaledImage(pages[pageIndex], pngImage, {
                  x: rect.x, y: rect.y, w: rect.width, h: rect.height,
                })
              }
            }
          } catch (err) {
            console.error("Failed to embed native signature for", fieldName, err)
          }
        }
      }

      // Flatten the form
      try {
        pdfForm.flatten()
      } catch (_) {}

      const filledBytes = await pdfDoc.save()

      // Compute signature hashes client-side to avoid sending massive base64 over websocket
      const sigMeta = []
      for (const s of signatures) {
        const encoder = new TextEncoder()
        const data = encoder.encode(s.data_url)
        const hashBuf = await crypto.subtle.digest("SHA-256", data)
        const hashHex = Array.from(new Uint8Array(hashBuf))
          .map(b => b.toString(16).padStart(2, "0"))
          .join("")
        sigMeta.push({ field_id: s.field_id, field_name: s.field_name, signature_hash: hashHex })
      }

      // Encode PDF as base64 in chunks to avoid stack overflow on large files
      let base64Pdf = ""
      const chunk = 8192
      for (let i = 0; i < filledBytes.length; i += chunk) {
        base64Pdf += String.fromCharCode(...filledBytes.subarray(i, i + chunk))
      }
      base64Pdf = btoa(base64Pdf)

      this.pushEvent("custom_submit", {
        field_values: fieldValues,
        signatures: sigMeta,
        esign_consent: sigMeta.length > 0,
        filled_pdf_base64: base64Pdf,
      })
    } catch (err) {
      console.error("Hybrid submit error:", err)
      this.pushEvent("pdf_error", { message: "Failed to process form: " + err.message })
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.innerHTML = `Submit Form`
      }
    }
  },

  _drawScaledImage(page, image, { x, y, w, h }) {
    const dims = image.scale(1)
    const s = Math.min(w / dims.width, h / dims.height)
    const drawW = dims.width * s
    const drawH = dims.height * s
    page.drawImage(image, {
      x: x + (w - drawW) / 2,
      y: y + (h - drawH) / 2,
      width: drawW,
      height: drawH,
    })
  },
}

export default NativeFormSubmit
